import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { SHIP } from "@/constants/testIds";
import StatusBadge from "@/components/app/StatusBadge";
import { Truck } from "lucide-react";
import { usePreflightGuard } from "@/hooks/usePreflightGuard";
import { ScheduleWhatsAppButton } from "@/components/whatsapp/ScheduleWhatsAppButton";
import { SendWhatsAppButton } from "@/components/whatsapp/WaComposer";
import { DispatchWhatsAppButton } from "@/components/dispatch/DispatchWhatsAppDialog";
import { PageHeader, PrimaryAction } from "@/components/os/PagePattern";
import { PackageCheck } from "lucide-react";

const toText = (v) => {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
  if (typeof v === "object") {
    const parts = [v.address_line1, v.address_line2, v.city, v.state, v.country, v.postal_code, v.pincode]
      .filter((p) => typeof p === "string" && p.trim());
    if (parts.length) return parts.join(", ");
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  return String(v);
};

const NEXT_STATUS = {
  packed: "dispatched",
  dispatched: "in_transit",
  in_transit: "out_for_delivery",
  out_for_delivery: "delivered",
  delivered: "closed",
};

export default function Shipments() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const { runGuarded, dialog: preflightDialog } = usePreflightGuard();

  const load = () => {
    setLoading(true);
    api.get("/shipments", { params: { status: status !== "all" ? status : undefined, limit: 100 } })
      .then(({ data }) => setRows(data.items || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, [status]);

  const advance = async (row) => {
    const next = NEXT_STATUS[row.status];
    if (!next) return;
    await runGuarded({
      entity_kind: "shipment", entity_id: row.id, action: `set_${next}`,
      call: () => api.post(`/shipments/${row.id}/status`, { status: next }),
      onSuccess: () => { toast.success(`Shipment ${next}`); load(); },
    });
  };

  return (
    <div className="space-y-4 max-w-[1600px]">
      <PageHeader eyebrow="Orders & Dispatch" title="Shipments" description="Track every forward shipment from packing to proof of delivery."
        primary={<PrimaryAction to="/logistics/fulfilment" icon={PackageCheck} testid="ship-open-fulfilment">Fulfilment & Dispatch</PrimaryAction>}
        secondary={<ScheduleWhatsAppButton kind="shipment" />} />

      <div className="bg-white border border-slate-200 rounded-md p-3 flex gap-2 items-center">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-56"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            {["all", "packed", "dispatched", "in_transit", "out_for_delivery", "delivered", "closed"].map((s) => (
              <SelectItem key={s} value={s}>
                <span className="capitalize">{s.replace(/_/g, " ")}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <Table data-testid={SHIP.table}>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-[10px] uppercase text-slate-500">Shipment</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Subsidiary</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Employee</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Device</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Courier</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">AWB</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Status</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-sm text-slate-500">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-sm text-slate-500">No shipments yet.</TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.id} data-testid={SHIP.row(r.id)} className="hover:bg-slate-50">
                <TableCell className="font-mono text-xs">{toText(r.shipment_code)}</TableCell>
                <TableCell className="text-sm text-slate-800" data-testid={`ship-subsidiary-${r.id}`}>
                  {r.company_name || r.master_company_name || <span className="text-slate-400">—</span>}
                </TableCell>
                <TableCell className="text-sm text-slate-800" data-testid={`ship-employee-${r.id}`}>
                  {r.employee_name ? (
                    <div className="min-w-0">
                      <div className="truncate max-w-[180px]">{r.employee_name}</div>
                      {r.employee_email && (
                        <div className="text-[10px] text-slate-400 font-mono truncate max-w-[180px]">{r.employee_email}</div>
                      )}
                    </div>
                  ) : r.contact_name ? (
                    <span className="text-slate-700">{toText(r.contact_name)}</span>
                  ) : <span className="text-slate-400">—</span>}
                </TableCell>
                <TableCell className="text-sm text-slate-700" data-testid={`ship-device-${r.id}`}>
                  {r.assets_summary?.length ? (
                    <div className="space-y-0.5">
                      {r.assets_summary.slice(0, 2).map((a, i) => (
                        <div key={i} className="text-[11px]">
                          <span className="text-slate-800">{[a.brand, a.model].filter(Boolean).join(" ") || a.category || "—"}</span>
                          {(a.serial || a.asset_tag) && (
                            <span className="text-[10px] text-slate-400 font-mono ml-1">· {a.serial || a.asset_tag}</span>
                          )}
                        </div>
                      ))}
                      {r.assets_summary.length > 2 && (
                        <div className="text-[10px] text-slate-400">+{r.assets_summary.length - 2} more</div>
                      )}
                    </div>
                  ) : (
                    <span className="text-slate-400">{Array.isArray(r.asset_ids) && r.asset_ids.length ? `${r.asset_ids.length} asset(s)` : "—"}</span>
                  )}
                </TableCell>
                <TableCell className="text-sm">{toText(r.courier) || "—"}</TableCell>
                <TableCell className="text-sm font-mono">{toText(r.awb) || "—"}</TableCell>
                <TableCell><div className="flex items-center gap-2"><StatusBadge value={typeof r.status === "string" ? r.status : toText(r.status)} />{["dispatched", "in_transit", "out_for_delivery"].includes(r.status) ? <DispatchWhatsAppButton kind="shipment" id={r.id} label={r.dispatch_notice?.state === "ACCEPTED" ? "WhatsApp sent ✓" : "WhatsApp dispatch update"} testid={`send-whatsapp-shipment-${r.id}`} onSent={load} /> : <SendWhatsAppButton record={{ kind: "shipment", id: r.id, code: toText(r.shipment_code) }} purpose="SHIPMENT" label="WhatsApp" testid={`send-whatsapp-shipment-${r.id}`} />}</div></TableCell>
                <TableCell>
                  {NEXT_STATUS[r.status] ? (
                    <Button
                      data-testid={SHIP.statusBtn}
                      size="sm"
                      variant="outline"
                      onClick={() => advance(r)}
                    >
                      <Truck className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                      Mark {NEXT_STATUS[r.status].replace(/_/g, " ")}
                    </Button>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {preflightDialog}
    </div>
  );
}
